import React, { useCallback, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppTheme } from '../hooks/useAppTheme';
import { useLivePoll } from '../hooks/useLivePoll';
import { getAnimatorView, animatorAction } from '../utils/liveApi';
import TernaryTriangle from '../components/live/TernaryTriangle';
import AnimatorStatsPanel from '../components/live/AnimatorStatsPanel';
import QrPanel from '../components/live/QrPanel';
import questionsData from '../data/orientation-axis-questions.json';

const POLL_INTERVAL_MS = 1000;

function buildJoinUrl(code) {
  if (typeof window === 'undefined') return `/live/${code}`;
  return `${window.location.origin}/live/${code}`;
}

export default function LiveAnimatorPage() {
  const { code: rawCode } = useParams();
  const code = (rawCode || '').toUpperCase();
  const { user } = useAuth();
  const { isE3D, toggleTheme } = useAppTheme();
  // In dev, fall back to a default email so the flow works without auth
  // (matches api/utils/swa-auth.js which accepts any caller in dev mode).
  const animatorEmail =
    user?.email ||
    (process.env.NODE_ENV === 'development' ? 'dev-animator@localhost' : null);

  const [actionInFlight, setActionInFlight] = useState(false);
  const [actionError, setActionError] = useState(null);

  const fetcher = useCallback(
    ({ knownVersion, signal }) =>
      getAnimatorView({ code, knownVersion, animatorEmail, signal }),
    [code, animatorEmail]
  );

  const { data, error, refetch } = useLivePoll(fetcher, POLL_INTERVAL_MS, {
    enabled: !!code && !!animatorEmail
  });

  const currentQuestion = useMemo(() => {
    if (!data) return null;
    if (data.state === 'question' || data.state === 'revealed') {
      return questionsData.questions[data.currentQuestionIndex];
    }
    return null;
  }, [data]);

  const axesLabels = useMemo(
    () => ({
      innocence: questionsData.axes[0].shortLabel,
      honneur: questionsData.axes[1].shortLabel,
      puissance: questionsData.axes[2].shortLabel
    }),
    []
  );

  const handleAction = useCallback(
    async (action) => {
      setActionError(null);
      setActionInFlight(true);
      try {
        await animatorAction({ code, action, animatorEmail });
        await refetch();
      } catch (e) {
        const resp = e.response;
        if (resp && resp.status === 409) {
          setActionError("L'état a changé — synchronisation…");
          await refetch();
        } else {
          setActionError(resp?.data?.error || e.message || 'Erreur inconnue');
        }
      } finally {
        setActionInFlight(false);
      }
    },
    [code, animatorEmail, refetch]
  );

  if (!animatorEmail) {
    return (
      <div className="container" style={{ marginTop: '2rem' }}>
        <p>Connexion requise pour animer une session.</p>
        <Link to="/auth" className="btn btn-secondary">Se connecter</Link>
      </div>
    );
  }

  if (error && !data) {
    const status = error?.response?.status;
    return (
      <div className="container" style={{ marginTop: '2rem' }}>
        <h2>Session inaccessible</h2>
        {status === 404 && <p>Aucune session ne correspond au code <code>{code}</code>.</p>}
        {status === 403 && <p>Vous n'êtes pas l'animateur de cette session.</p>}
        {!status && <p>{error.message || 'Erreur réseau.'}</p>}
        <Link className="btn btn-secondary" to="/live">Retour</Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container" style={{ marginTop: '2rem', textAlign: 'center' }}>
        <div className="loading">Chargement de la session…</div>
      </div>
    );
  }

  const { state, currentQuestionIndex, totalQuestions, stats, positions, centroid } = data;
  const joinUrl = buildJoinUrl(code);

  return (
    <div className="container" style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem', flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0 }}>Session live · {code}</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ color: 'var(--color-text-muted, #7f8c8d)', fontSize: '0.9rem' }}>
            {questionsData.title}
          </span>
          <button
            type="button"
            onClick={toggleTheme}
            className="btn btn-secondary"
            style={{ fontSize: '0.85rem', padding: '0.35rem 0.75rem' }}
            title="Basculer entre le thème par défaut et le thème de la conférence E3D"
          >
            🎨 {isE3D ? 'Style par défaut' : 'Style conférence'}
          </button>
        </div>
      </div>

      <AnimatorStatsPanel
        state={state}
        currentQuestionIndex={currentQuestionIndex}
        totalQuestions={totalQuestions}
        stats={stats}
      />

      {actionError && (
        <div style={{ padding: '0.75rem 1rem', background: '#fdecea', color: '#c0392b', borderRadius: 6 }}>
          {actionError}
        </div>
      )}

      {state === 'lobby' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', alignItems: 'start' }}>
          <QrPanel code={code} joinUrl={joinUrl} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div className="card">
              <h3>Avant de démarrer</h3>
              <p>
                Partagez le code <strong>{code}</strong> ou le QR code aux participants.
                Quand tout le monde est connecté, démarrez la première question.
              </p>
              <p style={{ color: 'var(--color-text-muted, #7f8c8d)', fontSize: '0.9rem' }}>
                Participants connectés : <strong>{stats.connectedCount}</strong> / {stats.totalCount}
              </p>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => handleAction('startFromLobby')}
              disabled={actionInFlight}
              style={{ fontSize: '1.1rem', padding: '1rem' }}
            >
              Démarrer la session
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => handleAction('end')}
              disabled={actionInFlight}
            >
              Annuler la session
            </button>
          </div>
        </div>
      )}

      {(state === 'question' || state === 'revealed') && currentQuestion && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', alignItems: 'start' }}>
          <div className="card">
            <h3 style={{ marginTop: 0 }}>{currentQuestion.text}</h3>
            <ol style={{ paddingLeft: '1.25rem', lineHeight: 1.6 }}>
              {currentQuestion.options.map((opt, idx) => (
                <li key={idx}>
                  {opt.text}{' '}
                  <span style={{ color: 'var(--color-text-muted, #bdc3c7)', fontSize: '0.85em' }}>
                    ({questionsData.axes[opt.axis].shortLabel})
                  </span>
                </li>
              ))}
            </ol>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
              {state === 'question' && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleAction('reveal')}
                  disabled={actionInFlight}
                >
                  Révéler les positions
                </button>
              )}
              {state === 'revealed' && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleAction('advance')}
                  disabled={actionInFlight}
                >
                  {currentQuestionIndex + 1 >= totalQuestions ? 'Terminer la session' : 'Question suivante →'}
                </button>
              )}
              {state === 'question' && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleAction('advance')}
                  disabled={actionInFlight}
                  title="Passer sans révéler"
                >
                  Passer
                </button>
              )}
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => {
                  if (window.confirm('Terminer la session maintenant ?')) handleAction('end');
                }}
                disabled={actionInFlight}
              >
                Terminer la session
              </button>
            </div>
          </div>

          <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
            <h3 style={{ margin: 0 }}>
              {state === 'revealed' ? 'Position cumulative du groupe' : 'En attente de la révélation…'}
            </h3>
            <TernaryTriangle
              points={state === 'revealed' ? positions || [] : []}
              centroid={state === 'revealed' ? centroid : null}
              axesLabels={axesLabels}
              size={420}
            />
            {state === 'revealed' && positions && (
              <div style={{ display: 'flex', gap: '1rem', fontSize: '0.85rem', color: 'var(--color-text-muted, #7f8c8d)' }}>
                <span><span style={{ color: 'var(--participant-fill, #3498db)' }}>●</span> participant</span>
                <span><span style={{ color: 'var(--centroid-stroke, #27ae60)' }}>○</span> moyenne du groupe</span>
                <span>{positions.length} position{positions.length > 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {state === 'ended' && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
          <h2 style={{ margin: 0 }}>Session terminée</h2>
          <p style={{ color: 'var(--color-text-muted, #7f8c8d)', margin: 0 }}>
            {positions ? `${positions.length} participant${positions.length > 1 ? 's' : ''}` : ''} — chacun a reçu sa position personnelle sur son téléphone.
          </p>
          <TernaryTriangle
            points={positions || []}
            centroid={centroid}
            axesLabels={axesLabels}
            size={500}
          />
          <Link to="/live" className="btn btn-secondary">Retour</Link>
        </div>
      )}
    </div>
  );
}
